import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

/**
 * Patient Profile Slice
 * Redux state management for patient profile data.
 * Used by doctors to view patient information before visit.
 *
 * @author Civilhealth Development Team
 */

// Types
export interface VisitSummary {
  visitDate: string;
  doctorName: string;
  doctorSpecialization: string | null;
  chiefComplaints: string | null;
  diagnosis: string | null;
  prescriptions: string | null;
  visitType: string;
  followUpDate: string | null;
  vitalSigns: string | null;
  treatmentPlan: string | null;
  reportId: number | null;
}

export interface PrescriptionSummary {
  prescriptionId: number | null;
  prescriptionDate: string;
  doctorName: string;
  diagnosis: string | null;
  medicationsCount: number;
  medications: string | null;
  validUntil: string | null;
}

export interface PatientProfile {
  // Basic Information
  patientId: number;
  patientType: string;
  name: string;
  age: number | null;
  gender: string | null;
  bloodGroup: string | null;
  mobile: string | null;
  email: string | null;
  photoUrl: string | null;

  // Address Information
  fullAddress: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  district: string | null;
  state: string | null;
  pincode: string | null;
  landmark: string | null;

  // Relationship (for family members)
  relationship: string | null;
  relationshipDisplayName: string | null;

  // Officer Information
  officerName: string | null;
  officerDesignation: string | null;
  officerDepartment: string | null;
  officerMobile: string | null;

  // Medical Information
  allergies: string | null;
  chronicConditions: string | null;
  currentMedications: string | null;
  medicalHistory: string | null;

  // Emergency Contact
  emergencyContactName: string | null;
  emergencyContactPhone: string | null;
  emergencyContactRelation: string | null;
  emergencyContactEmail: string | null;
  emergencyNotes: string | null;

  // Visit History
  visitHistory: VisitSummary[];

  // Prescription History
  prescriptionHistory: PrescriptionSummary[];

  // Appointment Context
  bookingNumber: string | null;
  appointmentDate: string | null;
  appointmentTime: string | null;
  reasonForVisit: string | null;
  chiefComplaints: string | null;
  symptoms: string | null;
  specialNotes: string | null;
  appointmentType: string | null;
  priority: string | null;
}

interface PatientProfileState {
  profile: PatientProfile | null;
  loading: boolean;
  error: string | null;
}

// Initial state
const initialState: PatientProfileState = {
  profile: null,
  loading: false,
  error: null,
};

// Async thunks
/**
 * Fetch patient profile by appointment ID
 * Doctor can view complete patient information before visit
 */
export const fetchPatientProfile = createAsyncThunk(
  'patientProfile/fetchPatientProfile',
  async (appointmentId: number, { rejectWithValue }) => {
    try {
      const response = await api.get(`/patient-profile/appointment/${appointmentId}`);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch patient profile');
    }
  }
);

/**
 * Fetch patient profile summary (lightweight version)
 */
export const fetchPatientProfileSummary = createAsyncThunk(
  'patientProfile/fetchPatientProfileSummary',
  async (appointmentId: number, { rejectWithValue }) => {
    try {
      const response = await api.get(`/patient-profile/appointment/${appointmentId}/summary`);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch patient summary');
    }
  }
);

// Slice
const patientProfileSlice = createSlice({
  name: 'patientProfile',
  initialState,
  reducers: {
    clearPatientProfile: (state) => {
      state.profile = null;
      state.error = null;
    },
    clearPatientProfileError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch patient profile
      .addCase(fetchPatientProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPatientProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload;
      })
      .addCase(fetchPatientProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch patient profile summary
      .addCase(fetchPatientProfileSummary.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPatientProfileSummary.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload;
      })
      .addCase(fetchPatientProfileSummary.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearPatientProfile, clearPatientProfileError } = patientProfileSlice.actions;
export default patientProfileSlice.reducer;